"use client";

import { useEffect, useLayoutEffect, useRef, useState } from "react";

import { WhatsAppIcon } from "@/app/components/icons/WhatsAppIcon";
import { landingWhatsAppHref } from "@/src/landing/whatsapp-cta";
import { trackEvent } from "@/src/lib/gtm-track";

const HERO_SECTION_ID = "inicio";

const FAB_OFFSET_X_MOBILE = 16;
const FAB_OFFSET_Y_MOBILE = 20;
const FAB_OFFSET_X_DESKTOP = 28;
const FAB_OFFSET_Y_DESKTOP = 28;

const BALL_SIZE_MOBILE = 52;
const BALL_SIZE_DESKTOP = 56;

const FAB_W_MOBILE = 162;
const FAB_W_DESKTOP = 184;
const FAB_H_MOBILE = 52;
const FAB_H_DESKTOP = 56;

const DURATION_ENTER_MS = 700;
const DURATION_EXIT_MS = 600;

type Phase = "hidden" | "entering" | "visible" | "exiting";

function isMobileViewport() {
  return typeof window !== "undefined" && window.innerWidth < 640;
}

function getFabGeometry() {
  const mobile = isMobileViewport();
  const offX = mobile ? FAB_OFFSET_X_MOBILE : FAB_OFFSET_X_DESKTOP;
  const offY = mobile ? FAB_OFFSET_Y_MOBILE : FAB_OFFSET_Y_DESKTOP;
  const w = mobile ? FAB_W_MOBILE : FAB_W_DESKTOP;
  const h = mobile ? FAB_H_MOBILE : FAB_H_DESKTOP;
  const ballSize = mobile ? BALL_SIZE_MOBILE : BALL_SIZE_DESKTOP;
  const x = window.innerWidth - w - offX;
  const y = window.innerHeight - h - offY;
  return { x, y, w, h, ballSize };
}

function applyVisibleStyles(el: HTMLElement) {
  const { x, y, w, h } = getFabGeometry();
  el.style.transform = `translate3d(${x}px, ${y}px, 0)`;
  el.style.width = `${w}px`;
  el.style.height = `${h}px`;
  el.style.borderRadius = "9999px";
  el.style.opacity = "1";
}

/** Bézier cuadrático: P(t) = (1-t)²·P0 + 2(1-t)t·Pc + t²·P1. */
function bezierQuad(
  t: number,
  p0: { x: number; y: number },
  pc: { x: number; y: number },
  p1: { x: number; y: number },
) {
  const u = 1 - t;
  return {
    x: u * u * p0.x + 2 * u * t * pc.x + t * t * p1.x,
    y: u * u * p0.y + 2 * u * t * pc.y + t * t * p1.y,
  };
}

/**
 * FAB de WhatsApp independiente del Hero. Aparece cuando el usuario sale del
 * Hero (#inicio) y se oculta al volver a entrar.
 *
 * Animación de entrada (premium):
 *   1. Bola pequeña entra desde arriba-derecha, fuera del viewport.
 *   2. Cae en arco curvo (apex hacia adentro de la pantalla).
 *   3. Pasa de largo del destino (squash) y rebota arriba (stretch).
 *   4. Se asienta y expande a pill, con el label "¡Comenzar ahora!" haciendo
 *      fade-in con un pequeño delay.
 *
 * Animación de salida (sobria):
 *   - Fade-out + scale-down rápido (~320 ms) sobre la propia esquina, sin
 *     viajar de vuelta. Es lo que se siente más natural cuando ya no hace
 *     falta y el Hero vuelve al viewport.
 *
 * Implementado con Web Animations API directamente porque necesitamos
 * keyframes intermedios con eases distintos por tramo (gravedad en la caída,
 * spring en el aterrizaje), algo que un solo `transition` o `spring` no
 * pueden expresar.
 */
export function WhatsAppFloatingCta() {
  const [phase, setPhase] = useState<Phase>("hidden");
  const elRef = useRef<HTMLAnchorElement>(null);
  const animRef = useRef<Animation | null>(null);
  const hasObservedRef = useRef(false);

  // Observer del Hero: dispara enter/exit.
  useEffect(() => {
    const heroSection = document.getElementById(HERO_SECTION_ID);
    if (!heroSection) return;

    const io = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (!entry) return;
        const heroVisible = entry.isIntersecting;

        if (!hasObservedRef.current) {
          hasObservedRef.current = true;
          if (!heroVisible) {
            // Aterrizó con scroll abajo: snap a visible sin animación.
            setPhase("visible");
          }
          return;
        }

        setPhase((prev) => {
          if (heroVisible && prev === "visible") return "exiting";
          if (!heroVisible && prev === "hidden") return "entering";
          return prev;
        });
      },
      { threshold: 0, rootMargin: "-72px 0px 0px 0px" },
    );
    io.observe(heroSection);
    return () => io.disconnect();
  }, []);

  // Snap a la geometría correcta cuando entramos a `visible` por primera vez
  // (caso "aterrizó scrolleado"), y reposicionamiento ante resize.
  useEffect(() => {
    if (phase !== "visible") return;
    const el = elRef.current;
    if (!el) return;
    if (!animRef.current) {
      applyVisibleStyles(el);
    }

    const handleResize = () => {
      if (animRef.current) return;
      applyVisibleStyles(el);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [phase]);

  // Animación con WAAPI.
  useLayoutEffect(() => {
    if (phase !== "entering" && phase !== "exiting") return;
    const el = elRef.current;
    if (!el) return;

    const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    animRef.current?.cancel();
    animRef.current = null;

    const { x: fabX, y: fabY, w: fabW, h: fabH, ballSize } = getFabGeometry();

    if (reduceMotion) {
      if (phase === "entering") {
        applyVisibleStyles(el);
        setPhase("visible");
      } else {
        el.style.opacity = "0";
        setPhase("hidden");
      }
      return;
    }

    let keyframes: Keyframe[];
    let duration: number;

    if (phase === "entering") {
      const ballXFab = fabX + (fabW - ballSize) / 2;
      const ballYFab = fabY + (fabH - ballSize) / 2;

      // Trayectoria como Bézier cuadrático con punto de control fuera de la
      // diagonal recta. Sampleamos varios puntos de la curva como keyframes,
      // así el browser nunca "rompe" en un vértice y la trayectoria se ve
      // continua. La aceleración (gravedad) la damos distribuyendo los
      // offsets de tiempo con una función power < 1 (las muestras finales
      // quedan más juntas en el tiempo → más velocidad al caer).
      const start = {
        x: window.innerWidth + 30,
        y: window.innerHeight * 0.5 - ballSize / 2,
      };
      const control = {
        x: ballXFab - 24,
        y: window.innerHeight * 0.7 - ballSize / 2,
      };
      const end = { x: ballXFab, y: ballYFab };

      const TRAVEL_END = 0.76; // hasta dónde dura el viaje (resto = bounce/expand).
      const SAMPLES = 12;
      const FADE_IN_T = 0.12; // primeros 12% del path: bola haciendo fade-in.
      const TIME_POWER = 1.5; // <1 acelera al final (gravedad sutil).

      const sampled: Keyframe[] = [];
      for (let i = 0; i <= SAMPLES; i++) {
        const t = i / SAMPLES;
        const offset = TRAVEL_END * Math.pow(t, TIME_POWER);
        const p = bezierQuad(t, start, control, end);
        const opacity = t < FADE_IN_T ? t / FADE_IN_T : 1;

        // Último sample (t=1) llega al destino y aplicamos el squash de
        // impacto en el mismo keyframe — sin frenar antes.
        const isImpact = i === SAMPLES;
        const ty = isImpact ? p.y + 14 : p.y;
        const scaleSuffix = isImpact ? " scale(1.12, 0.78)" : "";

        sampled.push({
          offset,
          width: `${ballSize}px`,
          height: `${ballSize}px`,
          transform: `translate3d(${p.x}px, ${ty}px, 0)${scaleSuffix}`,
          borderRadius: "9999px",
          opacity,
          // Linear entre samples del Bézier: la curvatura ya está codificada
          // en las posiciones, no queremos easings que distorsionen la
          // velocidad y generen frenadas perceptibles.
          easing: "linear",
        });
      }
      // El easing del último sample controla el segmento impact→bounce.
      sampled[sampled.length - 1].easing = "cubic-bezier(0.4, 0, 0.2, 1)";

      keyframes = [
        ...sampled,
        // Bounce: rebote arriba con stretch vertical.
        {
          offset: 0.88,
          width: `${ballSize}px`,
          height: `${ballSize}px`,
          transform: `translate3d(${ballXFab}px, ${ballYFab - 6}px, 0) scale(0.94, 1.08)`,
          borderRadius: "9999px",
          opacity: 1,
          easing: "cubic-bezier(0.4, 0, 0.2, 1)",
        },
        // Settle + expand a pill.
        {
          offset: 1,
          width: `${fabW}px`,
          height: `${fabH}px`,
          transform: `translate3d(${fabX}px, ${fabY}px, 0)`,
          borderRadius: "9999px",
          opacity: 1,
        },
      ];
      duration = DURATION_ENTER_MS;
    } else {
      // Exit: animación inversa del entering. La pill se contrae a bola en
      // la esquina, sube en arco por la misma curva Bézier y se desvanece
      // off-screen por el costado derecho.
      const ballXFab = fabX + (fabW - ballSize) / 2;
      const ballYFab = fabY + (fabH - ballSize) / 2;

      // Mismos puntos del Bézier que entering — recorremos la curva en
      // sentido inverso (t va de 1 a 0).
      const start = {
        x: window.innerWidth + 30,
        y: window.innerHeight * 0.5 - ballSize / 2,
      };
      const control = {
        x: ballXFab - 24,
        y: window.innerHeight * 0.7 - ballSize / 2,
      };
      const end = { x: ballXFab, y: ballYFab };

      const COLLAPSE_END = 0.18; // 0-18% del tiempo: pill→bola en la esquina.
      const SAMPLES = 12;
      const FADE_OUT_U_START = 0.75; // a partir de 75% del path empieza el fade.
      // >1 = ease-out: arranca rápida y va perdiendo velocidad al subir,
      // como una bola lanzada hacia arriba que desacelera.
      const TIME_POWER_EXIT = 1.25;

      const exitKeyframes: Keyframe[] = [
        // 0% — pill en la esquina, opacidad 1.
        {
          offset: 0,
          width: `${fabW}px`,
          height: `${fabH}px`,
          transform: `translate3d(${fabX}px, ${fabY}px, 0)`,
          borderRadius: "9999px",
          opacity: 1,
          easing: "cubic-bezier(0.4, 0, 0.2, 1)",
        },
      ];

      for (let i = 0; i <= SAMPLES; i++) {
        const u = i / SAMPLES; // u: progreso del path desde la esquina hacia off-screen
        const offset =
          COLLAPSE_END + (1 - COLLAPSE_END) * Math.pow(u, TIME_POWER_EXIT);
        const t = 1 - u; // recorrido inverso del Bézier
        const p = bezierQuad(t, start, control, end);
        const opacity =
          u >= FADE_OUT_U_START
            ? Math.max(0, 1 - (u - FADE_OUT_U_START) / (1 - FADE_OUT_U_START))
            : 1;

        exitKeyframes.push({
          offset,
          width: `${ballSize}px`,
          height: `${ballSize}px`,
          transform: `translate3d(${p.x}px, ${p.y}px, 0)`,
          borderRadius: "9999px",
          opacity,
          easing: "linear",
        });
      }

      keyframes = exitKeyframes;
      duration = DURATION_EXIT_MS;
    }

    const anim = el.animate(keyframes, {
      duration,
      easing: "linear",
      fill: "forwards",
    });
    animRef.current = anim;

    anim.onfinish = () => {
      try {
        anim.commitStyles();
      } catch {
        // El elemento puede haberse desmontado.
      }
      anim.cancel();
      animRef.current = null;
      setPhase((prev) => {
        if (prev === "entering") return "visible";
        if (prev === "exiting") return "hidden";
        return prev;
      });
    };

    return () => {
      animRef.current?.cancel();
      animRef.current = null;
    };
  }, [phase]);

  const isInteractive = phase === "visible";
  const isHidden = phase === "hidden";

  return (
    <a
      ref={elRef}
      data-wa-cta-fab
      data-phase={phase}
      href={landingWhatsAppHref()}
      target="_blank"
      rel="noopener noreferrer"
      onClick={() => trackEvent({ name: "click_whatsapp", params: { type: "fab_cta" } })}
      aria-label="Comenzar ahora por WhatsApp"
      aria-hidden={!isInteractive}
      tabIndex={isInteractive ? 0 : -1}
      className="wa-cta-fab inline-flex items-center justify-center overflow-hidden whitespace-nowrap bg-green-600 text-sm font-bold text-white shadow-lg shadow-green-600/40 ring-1 ring-inset ring-white/10 transition-colors hover:bg-green-500 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-cyan-400 sm:text-base"
      style={{
        position: "fixed",
        left: 0,
        top: 0,
        width: 0,
        height: 0,
        borderRadius: "9999px",
        opacity: isHidden ? 0 : 1,
        pointerEvents: isInteractive ? "auto" : "none",
        willChange: "transform, width, height, border-radius, opacity",
        zIndex: 60,
      }}
    >
      <WhatsAppIcon className="h-6 w-6 shrink-0" />
      <span className="wa-cta-fab-label">¡Comenzar ahora!</span>
    </a>
  );
}
